import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import {
  BR, PT, US, CA, MX, AR, CL, CO, PE, UY, PY, BO, EC, VE,
  ES, GB, FR, DE, IT, NL, BE, CH, AO, MZ, JP, CN, AU,
} from 'country-flag-icons/react/3x2'

// Bandeira SVG por ISO (renderiza no Windows, ao contrário de emoji). Importes nomeados = tree-shaken.
const FLAGS = {
  BR, PT, US, CA, MX, AR, CL, CO, PE, UY, PY, BO, EC, VE,
  ES, GB, FR, DE, IT, NL, BE, CH, AO, MZ, JP, CN, AU,
}

// DDI por país (iso, código, nome em PT). Brasil primeiro; demais ordenados por nome.
export const COUNTRIES = [
  { iso: 'BR', dial: '55', name: 'Brasil' },
  { iso: 'AO', dial: '244', name: 'Angola' },
  { iso: 'AR', dial: '54', name: 'Argentina' },
  { iso: 'AU', dial: '61', name: 'Austrália' },
  { iso: 'BE', dial: '32', name: 'Bélgica' },
  { iso: 'BO', dial: '591', name: 'Bolívia' },
  { iso: 'CA', dial: '1', name: 'Canadá' },
  { iso: 'CL', dial: '56', name: 'Chile' },
  { iso: 'CN', dial: '86', name: 'China' },
  { iso: 'CO', dial: '57', name: 'Colômbia' },
  { iso: 'EC', dial: '593', name: 'Equador' },
  { iso: 'ES', dial: '34', name: 'Espanha' },
  { iso: 'US', dial: '1', name: 'Estados Unidos' },
  { iso: 'FR', dial: '33', name: 'França' },
  { iso: 'NL', dial: '31', name: 'Holanda' },
  { iso: 'IT', dial: '39', name: 'Itália' },
  { iso: 'JP', dial: '81', name: 'Japão' },
  { iso: 'MX', dial: '52', name: 'México' },
  { iso: 'MZ', dial: '258', name: 'Moçambique' },
  { iso: 'PY', dial: '595', name: 'Paraguai' },
  { iso: 'PE', dial: '51', name: 'Peru' },
  { iso: 'PT', dial: '351', name: 'Portugal' },
  { iso: 'GB', dial: '44', name: 'Reino Unido' },
  { iso: 'CH', dial: '41', name: 'Suíça' },
  { iso: 'UY', dial: '598', name: 'Uruguai' },
  { iso: 'VE', dial: '58', name: 'Venezuela' },
  { iso: 'DE', dial: '49', name: 'Alemanha' },
]

const DEFAULT = COUNTRIES[0]

/** Quebra o valor armazenado (E.164 "+55..." ou legado só-dígitos) em { iso, national(dígitos) }. */
export function splitValue(value) {
  const v = String(value || '').trim()
  if (v.startsWith('+')) {
    const digits = v.slice(1).replace(/\D/g, '')
    // Casa o maior prefixo de DDI (ex.: 1 vs 55 vs 351).
    const byLen = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length)
    const match = byLen.find((c) => digits.startsWith(c.dial))
    if (match) return { iso: match.iso, national: digits.slice(match.dial.length) }
    return { iso: DEFAULT.iso, national: digits }
  }
  // Legado sem "+": assume Brasil; remove DDI 55 se já vier embutido (>11 dígitos).
  let digits = v.replace(/\D/g, '')
  if (digits.startsWith('55') && digits.length > 11) digits = digits.slice(2)
  return { iso: DEFAULT.iso, national: digits }
}

/**
 * Valida o número (parte após o DDI). Retorna uma CHAVE de i18n de erro ou null se ok/vazio.
 * Brasil: exige DDD + número = 10 (fixo/8 díg) ou 11 (celular/9 díg) dígitos.
 * Demais países: exige pelo menos 6 dígitos.
 */
export function validatePhone(value) {
  const v = String(value || '').trim()
  if (!v) return null // opcional; obrigatoriedade é tratada por quem usa (ex.: opt-in WhatsApp)
  const { iso, national } = splitValue(v)
  const len = national.replace(/\D/g, '').length
  if (len === 0) return 'phone.errorMissingNumber'
  if (iso === 'BR') {
    if (len < 10 || len > 11) return 'phone.errorBR'
  } else if (len < 6) {
    return 'phone.errorTooShort'
  }
  return null
}

/**
 * Input de telefone com dropdown de DDI + bandeiras. Emite E.164 "+<ddi><dígitos>" (ou "" se vazio).
 * @param {{ value?: string, onChange: (v: string) => void, placeholder?: string, id?: string,
 *           searchPlaceholder?: string, error?: string }} props
 */
export default function PhoneInput({ value, onChange, placeholder, id, searchPlaceholder, error }) {
  const [iso, setIso] = useState(DEFAULT.iso)
  const [national, setNational] = useState('')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const wrapRef = useRef(null)
  const lastEmitted = useRef(null)

  // Reinicializa só quando o valor muda por fora (não no nosso próprio emit).
  useEffect(() => {
    if (value === lastEmitted.current) return
    const parsed = splitValue(value)
    setIso(parsed.iso)
    setNational(parsed.national)
  }, [value])

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const country = COUNTRIES.find((c) => c.iso === iso) || DEFAULT

  const emit = (nextIso, nextNational) => {
    const c = COUNTRIES.find((x) => x.iso === nextIso) || DEFAULT
    const digits = String(nextNational || '').replace(/\D/g, '')
    const out = digits ? `+${c.dial}${digits}` : ''
    lastEmitted.current = out
    onChange(out)
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return COUNTRIES
    return COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.iso.toLowerCase().includes(q),
    )
  }, [query])

  const SelFlag = FLAGS[country.iso]
  const numberCls = `input flex-1 min-w-0${error ? ' border-red-500' : ''}`

  return (
    <div ref={wrapRef}>
      <div className="flex items-stretch gap-2">
        <div className="relative flex-shrink-0">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="input flex items-center gap-1.5 h-full"
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            {SelFlag && <SelFlag className="w-5 h-auto rounded-[2px]" title={country.name} />}
            <span className="text-sm whitespace-nowrap">+{country.dial}</span>
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </button>
          {open && (
            <div className="absolute z-30 mt-1 w-64 max-h-72 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
              <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={searchPlaceholder || 'Buscar país…'}
                    className="w-full pl-7 input py-1 text-sm"
                  />
                </div>
              </div>
              <ul className="py-1" role="listbox">
                {filtered.map((c) => {
                  const F = FLAGS[c.iso]
                  return (
                    <li key={c.iso}>
                      <button
                        type="button"
                        onClick={() => {
                          setIso(c.iso)
                          setOpen(false)
                          setQuery('')
                          emit(c.iso, national)
                        }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 text-left"
                      >
                        {F && <F className="w-5 h-auto rounded-[2px] flex-shrink-0" />}
                        <span className="flex-1 truncate">{c.name}</span>
                        <span className="text-gray-500">+{c.dial}</span>
                      </button>
                    </li>
                  )
                })}
                {filtered.length === 0 && <li className="px-3 py-2 text-sm text-gray-500">—</li>}
              </ul>
            </div>
          )}
        </div>
        <input
          id={id}
          type="tel"
          inputMode="numeric"
          value={national}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '')
            setNational(digits)
            emit(iso, digits)
          }}
          placeholder={placeholder}
          className={numberCls}
        />
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  )
}
